/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: trusted editor HTML */
"use client";
import { Dialog } from "@base-ui/react";
import { Avatar } from "@base-ui/react/avatar";
import { Menu } from "@base-ui/react/menu";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Flag, Pencil, Trash } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import verified from "#/assets/badge/verified-badge.svg";
import { uploadFile } from "@/actions/file.action";
import {
  deleteReplyAction,
  editReplyAction,
  postReplyReply,
  reportReplyAction,
} from "@/actions/forum.action";
import ChevronDown from "@/components/icons/chevron-down";
import ElipsisIcon from "@/components/icons/elipsis";
import MessageIcon from "@/components/icons/message";
import { Button } from "@/components/ui/button";
import TopicNestedReplies from "./topic-nested-replies";
import TopicReplyEditor from "./topic-reply-editor";
import TopicReplyLikeButton from "./topic-reply-like-button";
import type { Reply } from "./types";

const MAX_TEXT_LENGTH = 200;

type CurrentUser = { name: string; surname: string; imageUrl?: string };

interface Props {
  reply: Reply;
  topicId: string;
  isLoggedIn: boolean;
  currentUser?: CurrentUser;
}

const TopicReplyCard = ({ reply, topicId, isLoggedIn, currentUser }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [nestedRepliesExpanded, setNestedRepliesExpanded] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const queryClient = useQueryClient();
  const queryKey = ["forum-replies", topicId];

  const isLong = reply.text.length > MAX_TEXT_LENGTH;
  const displayText = `${reply.text.slice(0, MAX_TEXT_LENGTH)}...`;

  const initials = reply.userName
    .split(" ")
    .map((n) => n[0])
    .join("");

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ html, images }: { html: string; images: File[] }) => {
      let imageUrls: string[] | undefined;
      if (images.length > 0) {
        const results = await Promise.all(
          images.map((file) => uploadFile(file, "forum/replies")),
        );
        const urls = results.flatMap((r) => {
          return r.isSuccess ? [r.result.fileUrl] : [];
        });
        if (urls.length > 0) {
          imageUrls = urls;
        }
      }
      return postReplyReply(topicId, reply.id, html, imageUrls);
    },
    onSuccess: (res) => {
      if (res.isSuccess) {
        setShowReplyForm(false);
        setNestedRepliesExpanded(true);
        queryClient.invalidateQueries({
          queryKey: ["forum-nested-replies", reply.id],
        });
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  const { mutate: editMutate, isPending: isEditPending } = useMutation({
    mutationFn: async ({ html, images }: { html: string; images: File[] }) => {
      let newImageUrls: string[] = [];
      if (images.length > 0) {
        const results = await Promise.all(
          images.map((f) => uploadFile(f, "forum/replies")),
        );
        newImageUrls = results.flatMap((r) =>
          r.isSuccess ? [r.result.filePath] : [],
        );
      }
      return editReplyAction(reply.id, html, [
        ...reply.imageUrls,
        ...newImageUrls,
      ]);
    },
    onSuccess: (res) => {
      if (res.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        setIsEditing(false);
      }
    },
  });

  const handleReport = async () => {
    await reportReplyAction(reply.id);
    setReportOpen(false);
  };

  const { mutate: deleteMutate, isPending: isDeletePending } = useMutation({
    mutationFn: () => deleteReplyAction(reply.id),
    onSuccess: (res) => {
      if (res.isSuccess) {
        setDeleteOpen(false);
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  return (
    <div className="py-4 border-b border-neutral-100 last:border-0">
      <div className="flex gap-3">
        <Avatar.Root className="inline-flex size-10 items-center justify-center overflow-hidden rounded-full bg-gray-100 shrink-0">
          {reply.userAvatar && (
            <Avatar.Image
              className="size-full object-cover"
              src={reply.userAvatar}
            />
          )}
          <Avatar.Fallback className="flex size-full items-center justify-center text-sm font-medium text-gray-700">
            {initials}
          </Avatar.Fallback>
        </Avatar.Root>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <span className="inline-flex items-center gap-1 text-sm font-semibold">
                {reply.userActionUrl ? (
                  <Link
                    className="hover:underline"
                    href={`/committee/${reply.userActionUrl.split("=").at(-1)}`}
                  >
                    {reply.userName}
                  </Link>
                ) : (
                  reply.userName
                )}
                {reply.isVerified && (
                  <Image alt="verified" height={14} src={verified} width={14} />
                )}
                <span className="text-xs font-normal text-neutral-400 ml-1">
                  {reply.createdAtLabel}
                </span>
              </span>
              <p className="text-xs text-neutral-500">
                {reply.userTitle ?? "Üye"}
              </p>
            </div>

            <Menu.Root>
              <Menu.Trigger render={<Button size="icon-sm" variant="ghost" />}>
                <ElipsisIcon className="size-5" />
              </Menu.Trigger>
              <Menu.Portal>
                <Menu.Positioner align="end" sideOffset={4}>
                  <Menu.Popup className="min-w-40 rounded-xl border border-neutral-200 bg-white shadow-lg py-1 outline-none z-50">
                    {reply.isOwner ? (
                      <>
                        <Menu.Item
                          className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 cursor-pointer hover:bg-neutral-50 data-highlighted:bg-neutral-50 outline-none"
                          onClick={() => setIsEditing(true)}
                        >
                          <Pencil className="size-4 stroke-neutral-400" />
                          Düzenle
                        </Menu.Item>
                        <Menu.Item
                          className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 cursor-pointer hover:bg-red-50 data-highlighted:bg-red-50 outline-none"
                          onClick={() => setDeleteOpen(true)}
                        >
                          <Trash className="size-4" />
                          Sil
                        </Menu.Item>
                      </>
                    ) : (
                      <Menu.Item
                        className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 cursor-pointer hover:bg-neutral-50 data-highlighted:bg-neutral-50 outline-none"
                        onClick={() => setReportOpen(true)}
                      >
                        <Flag className="size-4 stroke-neutral-400" />
                        Bildir
                      </Menu.Item>
                    )}
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.Root>
          </div>

          <Dialog.Root onOpenChange={setReportOpen} open={reportOpen}>
            <Dialog.Portal>
              <Dialog.Backdrop className="fixed inset-0 bg-black/40 transition-all duration-150 data-starting-style:opacity-0 data-ending-style:opacity-0" />
              <Dialog.Popup className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl outline-none transition-all duration-150 data-starting-style:scale-90 data-starting-style:opacity-0 data-ending-style:scale-90 data-ending-style:opacity-0">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Yanıtı Bildir
                    </Dialog.Title>
                    <Dialog.Close
                      className="text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer"
                      render={<button type="button" />}
                    >
                      ✕
                    </Dialog.Close>
                  </div>
                  <Dialog.Description className="text-sm text-gray-500">
                    Bu yanıtı şikayet etmek istediğinizden emin misiniz?
                  </Dialog.Description>
                  <div className="flex gap-2 justify-end">
                    <Dialog.Close
                      render={
                        <Button size="sm" type="button" variant="outline" />
                      }
                    >
                      Vazgeç
                    </Dialog.Close>
                    <Button onClick={handleReport} size="sm" type="button">
                      Bildir
                    </Button>
                  </div>
                </div>
              </Dialog.Popup>
            </Dialog.Portal>
          </Dialog.Root>

          <Dialog.Root onOpenChange={setDeleteOpen} open={deleteOpen}>
            <Dialog.Portal>
              <Dialog.Backdrop className="fixed inset-0 bg-black/40 transition-all duration-150 data-starting-style:opacity-0 data-ending-style:opacity-0" />
              <Dialog.Popup className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl outline-none transition-all duration-150 data-starting-style:scale-90 data-starting-style:opacity-0 data-ending-style:scale-90 data-ending-style:opacity-0">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Yanıtı Sil
                    </Dialog.Title>
                    <Dialog.Close
                      className="text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer"
                      render={<button type="button" />}
                    >
                      ✕
                    </Dialog.Close>
                  </div>
                  <Dialog.Description className="text-sm text-gray-500">
                    Bu yanıtı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                  </Dialog.Description>
                  <div className="flex gap-2 justify-end">
                    <Dialog.Close
                      render={
                        <Button size="sm" type="button" variant="outline" />
                      }
                    >
                      Vazgeç
                    </Dialog.Close>
                    <Button
                      disabled={isDeletePending}
                      onClick={() => deleteMutate()}
                      size="sm"
                      type="button"
                      variant="destructive"
                    >
                      Sil
                    </Button>
                  </div>
                </div>
              </Dialog.Popup>
            </Dialog.Portal>
          </Dialog.Root>

          {isEditing ? (
            <div className="mt-2">
              <TopicReplyEditor
                autoFocus={true}
                initialContent={reply.text}
                isPending={isEditPending}
                onCancel={() => setIsEditing(false)}
                onSubmit={(html, images) => editMutate({ html, images })}
                placeholder="Yanıtını düzenle..."
              />
            </div>
          ) : (
            <div className="text-sm mt-2">
              {isLong && !expanded ? (
                <p className="text-neutral-700 leading-relaxed">
                  {displayText}
                  <button
                    className="inline-flex items-center gap-0.5 text-brand-blue-500 ml-1 hover:underline"
                    onClick={() => setExpanded(true)}
                    type="button"
                  >
                    Devamı
                    <ChevronDown className="size-4 stroke-brand-blue-500" />
                  </button>
                </p>
              ) : (
                <div>
                  <div
                    className="text-neutral-700 leading-relaxed prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: reply.text }}
                  />
                  {isLong && (
                    <button
                      className="inline-flex items-center gap-0.5 text-brand-blue-500 mt-1 hover:underline"
                      onClick={() => setExpanded(false)}
                      type="button"
                    >
                      Kapat
                      <ChevronDown className="size-4 stroke-brand-blue-500 rotate-180" />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mt-3">
            <TopicReplyLikeButton
              initialCount={reply.likeCount}
              initialLiked={reply.isLiked}
              queryKey={queryKey}
              replyId={reply.id}
            />
            <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
              <MessageIcon className="size-4 stroke-neutral-400" />
              {reply.replyCount}
            </span>
            {isLoggedIn && (
              <Button
                className="text-brand-blue-500 text-xs h-auto py-1"
                onClick={() => setShowReplyForm((v) => !v)}
                size="xs"
                variant="ghost"
              >
                Yanıtla
              </Button>
            )}
          </div>

          {showReplyForm && (
            <div className="mt-3">
              <TopicReplyEditor
                isPending={isPending}
                onCancel={() => setShowReplyForm(false)}
                onSubmit={(html, images) => mutate({ html, images })}
                placeholder="Yanıtını yaz..."
              />
            </div>
          )}

          {(reply.replyCount > 0 || nestedRepliesExpanded) && (
            <TopicNestedReplies
              currentUser={currentUser}
              expanded={nestedRepliesExpanded}
              isLoggedIn={isLoggedIn}
              onExpand={() => setNestedRepliesExpanded(true)}
              replyCount={reply.replyCount}
              replyId={reply.id}
              topicId={topicId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TopicReplyCard;
